import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useCompetitionHistory } from '@/features/competition/hooks';
import type { WcHistoryFinal, WcHistoryParticipant } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** How many finals to show before the "show all" toggle. */
const PREVIEW_COUNT = 4;

/**
 * Decide the winner of a final from the 365 cues. The winner is the lone
 * `isQualified` side; failing that, the side at `originPosition === 1`. Returns
 * null (→ rendered as two equal "Finalists") when the cue is missing or
 * ambiguous, so we never crown the wrong nation.
 */
function winnerIndex(participants: WcHistoryParticipant[]): number | null {
  if (participants.length !== 2) return null;
  const qualified = participants
    .map((participant, index) => (participant.isQualified ? index : -1))
    .filter((index) => index >= 0);
  if (qualified.length === 1) return qualified[0];
  const firstPos = participants.findIndex((participant) => participant.originPosition === 1);
  const otherHasPos = participants.some(
    (participant, index) => index !== firstPos && participant.originPosition === 1,
  );
  if (firstPos >= 0 && !otherHasPos) return firstPos;
  return null;
}

/** A nation chip: symbolic code badge + name, optionally crowned (the winner). */
function Finalist({
  participant,
  isWinner,
}: {
  participant: WcHistoryParticipant;
  isWinner: boolean;
}) {
  const palette = useThemePalette();
  const code = (participant.symbolicName ?? participant.name).slice(0, 3).toUpperCase();
  return (
    <View className="min-w-0 flex-1 flex-row items-center gap-2">
      <View
        className={`h-7 items-center justify-center rounded-md px-1.5 ${
          isWinner ? 'bg-volt' : 'bg-surface-2'
        }`}
      >
        <AppText
          variant="micro"
          weight="bold"
          tabular
          style={{ color: isWinner ? palette.onVolt : palette.muted }}
        >
          {code}
        </AppText>
      </View>
      <AppText
        variant="caption"
        weight={isWinner ? 'bold' : 'medium'}
        className={`min-w-0 shrink ${isWinner ? 'text-ink' : 'text-soft'}`}
        numberOfLines={1}
      >
        {participant.name}
      </AppText>
      {isWinner ? <Ionicons name="trophy" size={13} color={palette.captain} /> : null}
    </View>
  );
}

/** One past final row: season + the two finalists (winner highlighted). */
function FinalRow({ final, first }: { final: WcHistoryFinal; first: boolean }) {
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const winner = winnerIndex(final.participants);
  const [a, b] = final.participants;
  // Defensive: a final needs both finalists; skip a malformed row rather than
  // crash on an undefined participant (parseWcHistory should already drop these).
  if (!a || !b) return null;

  return (
    <View
      className={`flex-row items-center gap-3 py-2.5${first ? '' : ' border-t border-line'}`}
    >
      <View className="w-12">
        <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
          {final.seasonNum ? localizeDigits(String(final.seasonNum), arabicNumerals) : '—'}
        </AppText>
      </View>
      <View className="min-w-0 flex-1 flex-row items-center gap-2">
        <Finalist participant={a} isWinner={winner === 0} />
        <AppText variant="micro" weight="bold" className="shrink-0 text-muted">
          {t('history.versus')}
        </AppText>
        <Finalist participant={b} isWinner={winner === 1} />
      </View>
    </View>
  );
}

/**
 * World Cup History: a card listing past World Cup finals — the season and the
 * two finalists, with the winner highlighted (volt badge + trophy) when the
 * data lets us tell them apart, otherwise both shown neutrally as finalists.
 * Shows a short preview with a "show all / less" toggle. Self-hides while
 * loading-empty or when no history is synced.
 */
export function WcHistory() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { data } = useCompetitionHistory();
  const [expanded, setExpanded] = useState(false);

  const finals = useMemo(() => data ?? [], [data]);
  const shown = useMemo(
    () => (expanded ? finals : finals.slice(0, PREVIEW_COUNT)),
    [finals, expanded],
  );

  if (finals.length === 0) return null;
  const canExpand = finals.length > PREVIEW_COUNT;

  return (
    <View className="px-4">
      <Card className="px-4 py-4">
        <View className="mb-1 flex-row items-center gap-2">
          <Ionicons name="trophy-outline" size={16} color={palette.captain} />
          <AppText variant="caption" weight="bold" className="text-ink">
            {t('history.title')}
          </AppText>
        </View>
        <AppText variant="micro" className="mb-2 text-muted">
          {t('history.subtitle')}
        </AppText>

        <View>
          {shown.map((final, index) => (
            <FinalRow
              key={`${final.seasonNum ?? 'na'}-${index}`}
              final={final}
              first={index === 0}
            />
          ))}
        </View>

        {canExpand ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => setExpanded((value) => !value)}
            className="mt-1 flex-row items-center justify-center gap-1 py-2 active:opacity-70"
          >
            <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt-bright">
              {expanded ? t('history.showLess') : t('history.showAll', { count: finals.length })}
            </AppText>
            <Ionicons
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={14}
              color={palette.voltBright}
            />
          </Pressable>
        ) : null}
      </Card>
    </View>
  );
}
